import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Lock, Settings, ShieldCheck, Truck, User, UserPlus } from 'lucide-react'
import { useSesion } from '../store/sesion'
import { useDatos } from '../store/datos'
import { useUI } from '../store/ui'
import { Avatar, Boton, Campo, Entrada } from '../components/ui/base'
import { Hoja } from '../components/ui/Hoja'
import { Isotipo } from '../components/Logo'
import { cn } from '../lib/cn'
import { verificarClaveAdmin } from '../lib/claveAdmin'
import type { Usuario } from '../types'

/** Acceso de NovaBerry: sin clave y sin cuentas. Es una app interna con
 *  el link ya limitado a quien lo tenga, así que el único paso es decir
 *  quién eres, para que las ventas y la bitácora queden a tu nombre.
 *
 *  1. Primer perfil — solo la primera vez, cuando todavía no existe
 *     ningún perfil (instalación nueva).
 *  2. Elegir persona — el caso de todos los días: tocar el nombre. */
export function Entrar() {
  const { usuarios, listo } = useDatos()
  const navegar = useNavigate()

  if (!listo) return null
  if (usuarios.length === 0) return <PantallaPrimerPerfil />
  return <PantallaElegirPersona onEntrar={() => navegar('/')} />
}

/* ------------------------------ Marco ------------------------------ */

function Marco({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center superficie p-6">
      <div className="w-full max-w-sm anim-aparecer">
        <div className="mb-8 flex justify-center">
          <Isotipo className="size-16" />
        </div>
        {children}
      </div>
    </div>
  )
}

/* -------------------------- Primer perfil -------------------------- */

function PantallaPrimerPerfil() {
  const crearPrimeraPersona = useSesion((s) => s.crearPrimeraPersona)
  const navegar = useNavigate()
  const avisar = useUI((s) => s.avisar)
  const [nombre, setNombre] = useState('')
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState('')

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!nombre.trim()) return
    setCargando(true)
    setError('')
    try {
      await crearPrimeraPersona(nombre)
      avisar(`Bienvenido, ${nombre.trim().split(' ')[0]}`)
      navegar('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el perfil. Intenta de nuevo.')
    } finally {
      setCargando(false)
    }
  }

  return (
    <Marco>
      <div className="mb-2 flex justify-center">
        <span className="grid size-12 place-items-center rounded-2xl bg-lima-50 text-lima-700 dark:bg-lima-500/20 dark:text-lima-300">
          <ShieldCheck className="size-6" />
        </span>
      </div>
      <h1 className="text-center text-2xl font-bold texto-base">Todavía no hay nadie aquí</h1>
      <p className="mt-2 text-center text-[15px] texto-suave">
        Este es el primer arranque. Crea tu perfil de administrador para empezar; después podrás
        agregar al resto del equipo desde Vendedores.
      </p>

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-2xl bg-rojo-50 px-4 py-3 text-sm font-medium text-rojo-600 anim-aparecer dark:bg-rojo-500/10 dark:text-rojo-300"
        >
          {error}
        </p>
      )}

      <form onSubmit={enviar} className="mt-8 space-y-4">
        <Campo etiqueta="Tu nombre">
          <Entrada
            autoFocus
            required
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Felipe Arriagada"
          />
        </Campo>
        <Boton
          type="submit"
          tamano="lg"
          bloque
          cargando={cargando}
          disabled={!nombre.trim()}
          icono={!cargando && <ArrowRight className="size-5" />}
        >
          Crear perfil y entrar
        </Boton>
      </form>
    </Marco>
  )
}

/* ------------------------- Elegir persona ------------------------- */

function PantallaElegirPersona({ onEntrar }: { onEntrar: () => void }) {
  const usuarios = useDatos((s) => s.usuarios)
  const elegirPersona = useSesion((s) => s.elegirPersona)
  const entrarComoDespachos = useSesion((s) => s.entrarComoDespachos)
  const [entrando, setEntrando] = useState<string | null>(null)
  const [error, setError] = useState('')

  // Si el administrador tiene clave definida, tocar su nombre no entra
  // directo: abre este cuadro a pedir la clave primero. Sin clave
  // definida, sigue entrando igual que un vendedor —no se obliga a
  // nadie a poner una si no la quiere—.
  const [pidiendoClave, setPidiendoClave] = useState<Usuario | null>(null)
  const [clave, setClave] = useState('')
  const [errorClave, setErrorClave] = useState('')
  const [verificando, setVerificando] = useState(false)

  // El orden que se pidió: vendedores primero, Despachos después, y el
  // administrador al final —no es el que más se toca en el día a día.
  const vendedores = useMemo(
    () => usuarios.filter((u) => u.activo && u.rol === 'vendedor'),
    [usuarios],
  )
  const admins = useMemo(() => usuarios.filter((u) => u.activo && u.rol === 'admin'), [usuarios])
  const activos = vendedores.length + admins.length

  const tocar = async (id: string) => {
    const u = usuarios.find((x) => x.id === id)
    if (!u) return
    setEntrando(id)
    setError('')
    try {
      await elegirPersona(u)
      onEntrar()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo entrar. Intenta de nuevo.')
    } finally {
      setEntrando(null)
    }
  }

  const tocarAdmin = (u: Usuario) => {
    if (u.claveHash && u.claveSal) {
      setClave('')
      setErrorClave('')
      setPidiendoClave(u)
      return
    }
    void tocar(u.id)
  }

  const confirmarClave = async () => {
    if (!pidiendoClave?.claveHash || !pidiendoClave.claveSal) return
    setVerificando(true)
    setErrorClave('')
    try {
      const ok = await verificarClaveAdmin(clave, pidiendoClave.claveHash, pidiendoClave.claveSal)
      if (!ok) {
        setErrorClave('Clave incorrecta.')
        return
      }
      const u = pidiendoClave
      setPidiendoClave(null)
      await tocar(u.id)
    } finally {
      setVerificando(false)
    }
  }

  const tocarDespachos = async () => {
    setEntrando('despachos')
    setError('')
    try {
      await entrarComoDespachos()
      onEntrar()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo entrar. Intenta de nuevo.')
    } finally {
      setEntrando(null)
    }
  }

  const Fila = ({
    id,
    nombre,
    etiqueta,
    icono,
    iconoDerecho,
    nombreElemento,
    onClick,
  }: {
    id: string
    nombre: string
    etiqueta: string
    icono?: React.ReactNode
    iconoDerecho?: React.ReactNode
    nombreElemento?: React.ReactNode
    onClick: () => void
  }) => (
    <button
      onClick={onClick}
      disabled={entrando !== null}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border borde-suave superficie-elevada px-3 py-2 text-left',
        'transition-all duration-200 ease-[var(--ease-suave)] hover:sombra-carta hover:-translate-y-0.5 active:translate-y-0',
        entrando === id && 'opacity-60',
      )}
    >
      {icono ?? <Avatar nombre={nombre} tamano="sm" />}
      <span className="min-w-0 flex-1">
        {nombreElemento ?? (
          <span className="block truncate text-[14px] font-bold texto-base">{nombre}</span>
        )}
        <span className="block text-[11px] texto-suave">{etiqueta}</span>
      </span>
      {entrando === id ? (
        <span className="size-4 animate-spin rounded-full border-2 border-bosque-500 border-t-transparent" />
      ) : (
        (iconoDerecho ?? <User className="size-3.5 texto-tenue" />)
      )}
    </button>
  )

  return (
    <Marco>
      <img
        src="/logo-novaberry-wordmark.png"
        alt="NovaBerry — Naturalmente incomparable"
        className="mx-auto h-auto w-full max-w-[190px]"
      />

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-2xl bg-rojo-50 px-4 py-3 text-sm font-medium text-rojo-600 anim-aparecer dark:bg-rojo-500/10 dark:text-rojo-300"
        >
          {error}
        </p>
      )}

      <div className="mt-8 space-y-1.5">
        {vendedores.map((u) => (
          <Fila key={u.id} id={u.id} nombre={u.nombre} etiqueta="Vendedor" onClick={() => tocar(u.id)} />
        ))}
      </div>

      {/* Despachos y administración son otra cosa, no un vendedor más:
          una línea y un rótulo lo dejan claro de un vistazo. */}
      <div className="mt-5 mb-2 flex items-center gap-3">
        <span className="h-px flex-1" style={{ backgroundColor: 'var(--borde-suave)' }} />
        <span className="text-[11px] font-bold tracking-wide texto-tenue uppercase">Otros accesos</span>
        <span className="h-px flex-1" style={{ backgroundColor: 'var(--borde-suave)' }} />
      </div>

      <div className="space-y-1.5">
        <Fila
          id="despachos"
          nombre="Despachos"
          etiqueta="Ruta de reparto del día"
          icono={
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-tinta-50 text-tinta-600 dark:bg-tinta-500/20 dark:text-tinta-200">
              <Truck className="size-4" />
            </span>
          }
          iconoDerecho={<Truck className="size-3.5 texto-tenue" />}
          onClick={tocarDespachos}
        />

        {admins.map((u) => (
          <Fila
            key={u.id}
            id={u.id}
            nombre={u.nombre}
            etiqueta="Administrador"
            iconoDerecho={
              u.claveHash ? (
                <Lock className="size-3.5 texto-tenue" />
              ) : (
                <Settings className="size-3.5 texto-tenue" />
              )
            }
            nombreElemento={
              <span className="flex items-baseline gap-1 truncate font-display text-[15px] leading-none font-bold tracking-tight">
                <span className="text-bosque-500 dark:text-bosque-200">Nova</span>
                <span className="bg-gradient-to-r from-rojo-500 via-magenta-500 to-tinta-500 bg-clip-text text-transparent">
                  Berry
                </span>
              </span>
            }
            onClick={() => tocarAdmin(u)}
          />
        ))}
      </div>

      {activos === 0 && (
        <p className="mt-4 text-center text-sm texto-tenue">
          No hay perfiles activos. Pide a un administrador que te agregue desde Vendedores.
        </p>
      )}

      <p className="mt-8 flex items-center justify-center gap-1.5 text-center text-[12px] texto-tenue">
        <UserPlus className="size-3.5" />
        Los perfiles nuevos se crean desde Vendedores, dentro de la app.
      </p>

      <Hoja
        abierta={!!pidiendoClave}
        onCerrar={() => setPidiendoClave(null)}
        titulo="Clave de administrador"
        descripcion={pidiendoClave ? `Para entrar como ${pidiendoClave.nombre}` : undefined}
        ancho="sm"
        pie={
          <>
            <Boton variante="secundario" bloque onClick={() => setPidiendoClave(null)}>
              Cancelar
            </Boton>
            <Boton
              bloque
              cargando={verificando}
              disabled={clave.length !== 4}
              onClick={() => void confirmarClave()}
            >
              Entrar
            </Boton>
          </>
        }
      >
        <Campo etiqueta="Clave de 4 dígitos" requerido error={errorClave}>
          <Entrada
            inputMode="numeric"
            maxLength={4}
            value={clave}
            onChange={(e) => setClave(e.target.value.replace(/\D/g, '').slice(0, 4))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && clave.length === 4) void confirmarClave()
            }}
            placeholder="••••"
            className="codigo text-center text-xl tracking-[0.5em]"
            autoFocus
          />
        </Campo>
      </Hoja>
    </Marco>
  )
}
